import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { ExternalLink } from '../../../components';

import whatsappIcon from '../whatsapp.jpeg';

import css from './WhatsAppContactMenu.module.css';

/**
 * Shows the WhatsApp icon as a button. Clicking it reveals a small menu
 * letting the visitor choose between opening a WhatsApp chat or calling
 * the phone number directly - both work without needing a full modal.
 *
 * @component
 * @param {Object} props
 * @param {string} props.whatsapp - The phone number (used for wa.me and tel: links)
 * @param {string} [props.iconClassName] - Custom class for the icon image
 * @param {('up'|'down')} [props.openDirection] - Which way the menu opens relative to the icon
 * @param {boolean} [props.isAuthenticated=true] - Whether the current user is logged in. When false, clicking the icon calls `onRequireAuth` instead of opening the menu.
 * @param {Function} [props.onRequireAuth] - Called instead of opening the menu when `isAuthenticated` is false
 * @returns {JSX.Element|null}
 */
const WhatsAppContactMenu = props => {
  const {
    whatsapp,
    iconClassName,
    openDirection = 'down',
    isAuthenticated = true,
    onRequireAuth,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!whatsapp) {
    return null;
  }

  const handleIconClick = () => {
    if (!isAuthenticated) {
      onRequireAuth?.();
      return;
    }
    setIsOpen(prevIsOpen => !prevIsOpen);
  };

  return (
    <div className={css.root} ref={rootRef}>
      <button
        type="button"
        className={css.iconButton}
        onClick={handleIconClick}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <img src={whatsappIcon} alt="WhatsApp" className={iconClassName} />
      </button>
      {isOpen ? (
        <div
          className={classNames(css.menu, {
            [css.menuUp]: openDirection === 'up',
            [css.menuDown]: openDirection === 'down',
          })}
        >
          <ExternalLink
            href={`https://wa.me/${whatsapp}`}
            className={css.menuItem}
            onClick={() => setIsOpen(false)}
          >
            <FormattedMessage id="WhatsAppContactMenu.openWhatsApp" />
          </ExternalLink>
          <ExternalLink
            href={`tel:${whatsapp}`}
            target="_self"
            className={css.menuItem}
            onClick={() => setIsOpen(false)}
          >
            <FormattedMessage id="WhatsAppContactMenu.callPhoneNumber" />
          </ExternalLink>
        </div>
      ) : null}
    </div>
  );
};

export default WhatsAppContactMenu;
